import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useConstellation } from '../hooks/useConstellation';
import * as api from '../services/api';
import './InvestorLetters.css';

const InvestorLetters: React.FC = () => {
  const [availableLetters, setAvailableLetters] = useState<string[]>([]);
  const [selectedLetter, setSelectedLetter] = useState<string>('');
  const [letterContent, setLetterContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  
  // Add constellation background effect
  useConstellation();

  useEffect(() => {
    loadAvailableLetters();
  }, []);

  useEffect(() => {
    if (selectedLetter) {
      loadLetterContent();
    }
  }, [selectedLetter]);

  const loadAvailableLetters = async () => {
    try {
      const data = await api.getAvailableLetters();
      setAvailableLetters(data.letters);
      if (data.letters.length > 0) {
        setSelectedLetter(data.letters[0]);
      }
    } catch (err) {
      setError('Failed to load available letters');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadLetterContent = async () => {
    try {
      setLoading(true);
      const data = await api.getInvestorLetter(selectedLetter);
      setLetterContent(data.content);
    } catch (err) {
      setError('Failed to load letter content');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (month: string) => {
    const [year, monthNum] = month.split('.');
    const date = new Date(parseInt(year), parseInt(monthNum) - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  if (loading && availableLetters.length === 0) {
    return <div className="letters-loading">Loading investor letters...</div>;
  }

  return (
    <div className="investor-letters">
      <div className="letters-header">
        <h1 className="headline">Investor Letters</h1>
      </div>

      <div className="letters-container">
        <aside className="letters-sidebar">
          <h3 className="sidebar-title">Available Letters</h3>
          <div className="letter-list">
            {availableLetters.map(month => (
              <button
                key={month}
                className={`letter-item ${selectedLetter === month ? 'active' : ''}`}
                onClick={() => setSelectedLetter(month)}
              >
                {formatMonth(month)}
              </button>
            ))}
          </div>
        </aside>

        <main className="letter-content-area">
          {loading ? (
            <div className="letter-loading">Loading letter content...</div>
          ) : error ? (
            <div className="letter-error">{error}</div>
          ) : (
            <div className="letter-content">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => <h1 className="letter-h1">{children}</h1>,
                  h2: ({ children }) => <h2 className="letter-h2">{children}</h2>,
                  h3: ({ children }) => <h3 className="letter-h3">{children}</h3>,
                  p: ({ children }) => <p className="letter-p">{children}</p>,
                  ul: ({ children }) => <ul className="letter-ul">{children}</ul>,
                  ol: ({ children }) => <ol className="letter-ol">{children}</ol>,
                  li: ({ children }) => <li className="letter-li">{children}</li>,
                  strong: ({ children }) => <strong className="letter-strong">{children}</strong>,
                  em: ({ children }) => <em className="letter-em">{children}</em>,
                  hr: () => <hr className="letter-hr" />,
                }}
              >
                {letterContent}
              </ReactMarkdown>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default InvestorLetters;